import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useAsyncFn } from 'react-use';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import JobRolesControl from 'components/Forms/JobRolesControl';
import ModalBusyContainer from 'components/ModalBusyContainer';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { actions as modalActions } from 'redux/modal';
import { getJobsTasks } from 'http/jobsTasks';
import { applyStudentJobRoleChange, previewStudentJobRoleChange } from 'http/trainingSessions';
import { transformDataToSelectOptions } from 'utils';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    maxWidth: '100%',
  },
  intro: {
    marginBottom: theme.spacing(3),
  },
  selector: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(3),
  },
  tableWrapper: {
    overflowX: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
  },
  removeRow: {
    backgroundColor: 'rgba(211, 47, 47, 0.08)',
  },
  keepRow: {
    backgroundColor: 'rgba(46, 125, 50, 0.08)',
  },
  courseMeta: {
    color: theme.palette.text.secondary,
  },
  statusDanger: {
    color: theme.palette.error.main,
    fontWeight: 500,
  },
  statusNeutral: {
    color: theme.palette.text.secondary,
  },
  unchangedText: {
    color: theme.palette.text.secondary,
  },
  emptyState: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  btnContainer: {
    marginTop: theme.spacing(4),
    marginBottom: 0,
  },
  btn: {
    minWidth: '140px',
  },
  error: {
    marginTop: theme.spacing(2),
    color: theme.palette.error.main,
  },
  formControl: {
    minWidth: 240,
  },
}));

const isSameIdsSet = (leftIds, rightIds) => {
  if (leftIds.length !== rightIds.length) {
    return false;
  }

  const left = [...leftIds].sort((a, b) => a - b);
  const right = [...rightIds].sort((a, b) => a - b);
  return left.every((id, index) => id === right[index]);
};

const formatCourseMeta = (courseStartDate, courseEndDate, stationName) => {
  const parts = [];

  if (courseStartDate) {
    parts.push(courseEndDate ? `${courseStartDate} - ${courseEndDate}` : courseStartDate);
  }

  if (stationName) {
    parts.push(stationName);
  }

  return parts.join(' | ');
};

const StudentRoleChangeModal = ({ userId, initialJobTasks, onSuccess }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const initialJobTaskIds = useMemo(
    () => initialJobTasks.map(item => Number(item.value || item.id)).filter(value => !Number.isNaN(value)),
    [initialJobTasks],
  );
  const [jobTaskOptions, setJobTaskOptions] = useState([]);
  const [selectedJobTaskIds, setSelectedJobTaskIds] = useState(initialJobTaskIds);
  const [previewData, setPreviewData] = useState(null);
  const [keepEnrolledSessionIds, setKeepEnrolledSessionIds] = useState([]);
  const [stagedAssignments, setStagedAssignments] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [isReloading, setIsReloading] = useState(false);

  const closeModal = () => dispatch(modalActions.closeModal());

  const [jobTasksState, loadJobTasks] = useAsyncFn(async () => {
    const response = await getJobsTasks();
    if (response.items) {
      setJobTaskOptions(transformDataToSelectOptions(response.items, 'id', 'name'));
    }
    return response;
  }, []);

  const [previewState, fetchPreview] = useAsyncFn(
    async jobsTasksIds => {
      const response = await previewStudentJobRoleChange(userId, jobsTasksIds);
      if (!response.error) {
        setPreviewData(response);
        setErrorMessage('');
      } else {
        setPreviewData(null);
        setErrorMessage(response.message || t('errors.serverNotAvailable'));
      }
      return response;
    },
    [userId, t],
  );

  const [applyState, applyJobRoleChange] = useAsyncFn(
    async payload => {
      const response = await applyStudentJobRoleChange(
        userId,
        payload.jobsTasksIds,
        payload.keepEnrolledSessionIds,
        payload.assignSessionRows,
      );
      if (!response.error) {
        setIsReloading(true);
        try {
          await onSuccess();
          closeModal();
        } finally {
          setIsReloading(false);
        }
        return response;
      }

      setErrorMessage(response.message || t('errors.serverNotAvailable'));
      return response;
    },
    [closeModal, onSuccess, t, userId],
  );

  useEffect(() => {
    loadJobTasks();
  }, [loadJobTasks]);

  useEffect(() => {
    fetchPreview(selectedJobTaskIds);
  }, [fetchPreview, selectedJobTaskIds]);

  useEffect(() => {
    const removalRows = previewData?.currentAssignments || [];
    const requiredSessionRows = previewData?.requiredSessions?.items || [];

    setKeepEnrolledSessionIds(currentValue =>
      currentValue.filter(sessionId =>
        removalRows.some(row => row.trainingSessionId === sessionId),
      ),
    );

    setStagedAssignments(currentValue => {
      const nextValue = {};

      requiredSessionRows.forEach(row => {
        const trainingPartId = row.trainingPartId;
        const previousValue = currentValue[trainingPartId];
        const candidateIds = (row.candidates || []).map(candidate => Number(candidate.id));

        if (previousValue !== undefined && candidateIds.includes(previousValue)) {
          nextValue[trainingPartId] = previousValue;
          return;
        }

        if (candidateIds.length === 1) {
          nextValue[trainingPartId] = candidateIds[0];
        }
      });

      return nextValue;
    });
  }, [previewData]);

  const hasRoleChanges = !isSameIdsSet(initialJobTaskIds, selectedJobTaskIds);
  const currentAssignments = previewData?.currentAssignments || [];
  const requiredSessionRows =
    previewData?.requiredSessions?.items || [];
  const assignSessionRows = Object.keys(stagedAssignments)
    .map(trainingPartId => ({
      trainingPartId: Number(trainingPartId),
      trainingSessionId: stagedAssignments[trainingPartId] || null,
    }))
    .filter(item => item.trainingSessionId !== null);

  const isLoading = jobTasksState.loading || previewState.loading || applyState.loading || isReloading;
  const selectedJobTaskOptions = jobTaskOptions.filter(option => selectedJobTaskIds.includes(option.value));

  return (
    <div className={classes.root}>
      <ModalBusyContainer busy={isLoading}>
        <div className={classes.selector}>
          <JobRolesControl
            label={t('formLabels.jobsTasks')}
            options={jobTaskOptions}
            value={selectedJobTaskOptions}
            isDisabled={isLoading}
            onChange={value => {
              const nextValue = Array.isArray(value) ? value.map(item => Number(item.value)) : [];
              setSelectedJobTaskIds(nextValue);
            }}
            placeholder={t('formLabels.jobsTasks')}
          />
        </div>

        <Typography className={classes.sectionTitle} variant="subtitle1">
          {t('students.roleChangeCurrentAssignmentsTitle')}
        </Typography>
        <div className={classes.tableWrapper}>
          {currentAssignments.length ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('formLabels.course')}</TableCell>
                  <TableCell>{t('formLabels.state')}</TableCell>
                  <TableCell>{t('students.roleChangeAssignmentAction')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentAssignments.map(row => {
                  const sessionId = row.trainingSessionId;
                  const isAffected = !!row.isAffected;
                  const isKept = keepEnrolledSessionIds.includes(sessionId);
                  const rowClassName = isAffected ? (isKept ? classes.keepRow : classes.removeRow) : '';
                  const courseMeta = formatCourseMeta(row.courseStartDate, row.courseEndDate, row.stationName);

                  return (
                    <TableRow key={sessionId} className={rowClassName}>
                      <TableCell>
                        <Typography variant="body2">{row.courceTemplateDisplayName || row.courseName}</Typography>
                        {courseMeta ? (
                          <Typography className={classes.courseMeta} variant="caption">
                            {courseMeta}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <span
                          className={
                            !isAffected ? classes.unchangedText : isKept ? classes.statusNeutral : classes.statusDanger
                          }
                        >
                          {!isAffected
                            ? t('students.roleChangeAssignmentUnchanged')
                            : isKept
                            ? t('students.roleChangeKeptEnrolled')
                            : t('students.roleChangeWillBeArchived')}
                        </span>
                      </TableCell>
                      <TableCell>
                        {isAffected ? (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isKept}
                                color="primary"
                                disabled={isLoading}
                                onChange={() => {
                                  setKeepEnrolledSessionIds(currentValue =>
                                    currentValue.includes(sessionId)
                                      ? currentValue.filter(id => id !== sessionId)
                                      : [...currentValue, sessionId],
                                  );
                                }}
                              />
                            }
                            label={t('students.roleChangeKeepEnrolled')}
                          />
                        ) : (
                          <span className={classes.unchangedText}>-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className={classes.emptyState}>{t('students.roleChangeNoCurrentAssignments')}</div>
          )}
        </div>

        <Typography className={classes.sectionTitle} variant="subtitle1">
          {t('students.roleChangeNewAssignmentsTitle')}
        </Typography>
        <div className={classes.tableWrapper}>
          {requiredSessionRows.length ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('formLabels.trainingModule')}</TableCell>
                  <TableCell>{t('formLabels.course')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requiredSessionRows.map(row => {
                  const trainingPartId = row.trainingPartId;
                  const selectedSessionId = stagedAssignments[trainingPartId] ?? '';
                  const isSelectable = Array.isArray(row.candidates) && row.candidates.length > 0;

                  return (
                    <TableRow key={trainingPartId}>
                      <TableCell>
                        <Typography variant="body2">{row.courceTemplateDisplayName}</Typography>
                        <Typography className={classes.courseMeta} variant="caption">
                          {Array.isArray(row.roles) ? row.roles.join(', ') : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {isSelectable ? (
                          <FormControl className={classes.formControl} variant="outlined" size="small">
                            <Select
                              native={false}
                              value={selectedSessionId}
                              displayEmpty
                              disabled={isLoading}
                              onChange={event =>
                                setStagedAssignments(currentValue => ({
                                  ...currentValue,
                                  [trainingPartId]: event.target.value === '' ? null : Number(event.target.value),
                                }))
                              }
                            >
                              <MenuItem value="">{t('students.notAssigned')}</MenuItem>
                              {row.candidates.map(candidate => (
                                <MenuItem key={candidate.id} value={candidate.id}>
                                  {candidate.courceTemplateDisplayName || candidate.displayName}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : Array.isArray(row.assignedCourses) && row.assignedCourses.length ? (
                          row.assignedCourses.map(course => course.courceTemplateDisplayName || course.displayName).join(', ')
                        ) : (
                          t('commonValues.notAvailable')
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className={classes.emptyState}>{t('students.roleChangeNoNewAssignments')}</div>
          )}
        </div>

        {!!errorMessage && (
          <Typography className={classes.error} component="p">
            {Array.isArray(errorMessage) ? errorMessage.join(', ') : errorMessage}
          </Typography>
        )}

        <Grid container spacing={3} className={classes.btnContainer}>
          <Grid item xs={6}>
            <Typography align="right">
              <Button
                className={classes.btn}
                type="button"
                color="primary"
                variant="contained"
                disabled={!hasRoleChanges || isLoading}
                onClick={() =>
                  applyJobRoleChange({
                    jobsTasksIds: selectedJobTaskIds,
                    keepEnrolledSessionIds,
                    assignSessionRows,
                  })
                }
              >
                {t('buttons.save')}
              </Button>
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography align="left">
              <Button
                className={classes.btn}
                type="button"
                variant="outlined"
                disabled={isLoading}
                onClick={closeModal}
              >
                {t('buttons.cancel')}
              </Button>
            </Typography>
          </Grid>
        </Grid>
      </ModalBusyContainer>
    </div>
  );
};

StudentRoleChangeModal.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  initialJobTasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  onSuccess: PropTypes.func,
};

StudentRoleChangeModal.defaultProps = {
  initialJobTasks: [],
  onSuccess: async () => {},
};

export default StudentRoleChangeModal;
